import { __ } from '@wordpress/i18n'
import typeahead from 'typeahead-standalone'
;(function ($) {
	'use strict'

	/**
	 * ArdentLocations - A jQuery plugin for filtering and displaying locations with map integration
	 * @class
	 */
	class ArdentLocations {
		/**
		 * Default configuration
		 * @static
		 */
		static DEFAULTS = {
			filters: {
				serviceTypes: {
					enabled: false,
					dataFilter: null,
					data: null,
					title: null,
				},
				locationTypes: {
					enabled: false,
					dataFilter: null,
					data: null,
					title: null,
				},
				locationNames: {
					enabled: false,
					dataFilter: null,
					data: null,
					title: null,
				},
				cities: {
					enabled: false,
					dataFilter: null,
					data: null,
					title: null,
				},
			},
			locations: [],
			mapSettings: {
				colors: {},
			},
			pagination: {
				enabled: false,
				pageCount: 5,
				paged: false,
			},
			maxVisibleFilters: 5,
			debounceDelay: 300,
			cssClasses: {
				hidden: 'hide',
				selected: 'selected',
				disabled: 'disabled',
				fadeIn: 'fadeIn',
				fadeOut: 'fadeOut',
			},
			selectors: {
				stats: '.location__stats',
				list: '.location__list',
				map: '.location__map',
				item: '.location__item',
				noMatches: '.location__item__nomatches',
				count: '.location__count',
				filters: '.location__filters__container',
				listings: '.location__listings',
			},
		}

		/**
		 * @constructor
		 * @param {HTMLElement} element - The container element
		 * @param {Object} options - Plugin options
		 * @param {Object} Map - Google Maps Map class
		 * @param {Object} AdvancedMarkerElement - Google Maps AdvancedMarkerElement class
		 * @param {Object} PinElement - Google Maps PinElement class
		 */
		constructor(element, options, Map, AdvancedMarkerElement, PinElement) {
			this.element = element
			this.$element = $(element)
			this.targetId = this.$element.attr('id')

			// Merge settings
			this.settings = $.extend(true, {}, ArdentLocations.DEFAULTS, options)

			// Google Maps components
			this.mapComponents = { Map, AdvancedMarkerElement, PinElement }

			// State management
			this.state = {
				currentPage: 1,
				activeFilters: {},
				typeahead: {
					filterType: null,
					filterKey: null,
				},
				isInitialized: false,
			}

			// Map-related properties
			this.map = null
			this.markers = []
			this.bounds = null
			this.infowindow = null

			// Cache for DOM elements
			this.cache = {}

			// Event handlers (for cleanup)
			this.eventHandlers = {
				popstate: null,
				boundChanged: null,
			}

			// Validate configuration
			this.validateSettings()
		}

		/**
		 * Validate plugin settings
		 * @private
		 */
		validateSettings() {
			const { Map, AdvancedMarkerElement, PinElement } = this.mapComponents

			if (!Map || !AdvancedMarkerElement || !PinElement) {
				throw new Error('ArdentLocations: Google Maps components are required')
			}

			if (!this.targetId) {
				throw new Error('ArdentLocations: Container element must have an ID')
			}

			if (!Array.isArray(this.settings.locations)) {
				console.warn('ArdentLocations: locations should be an array, converting...')
				this.settings.locations = Object.values(this.settings.locations)
			}
		}

		/**
		 * Initialize the plugin
		 * @public
		 */
		init() {
			try {
				if (this.state.isInitialized) {
					console.warn('ArdentLocations: Already initialized')
					return this
				}

				if (this.settings.locations.length === 0) {
					console.warn('ArdentLocations: No locations provided')
					return this
				}

				// Capture any filter the visitor checked before the (delayed)
				// script loaded; syncWithURL() clears all radios below.
				const $preChecked = this.$element.find('.location__filters input[type=radio]:checked')

				this.cacheElements()
				this.initializeGoogleMaps()
				this.createFilters()
				this.setupTypeahead()
				this.bindEvents()
				this.syncWithURL(false)

				// Re-apply pre-init selections only if the URL didn't set filters.
				if ($.isEmptyObject(this.state.activeFilters)) {
					$preChecked.each((index, radio) => {
						this.setFilter(radio.name, radio.value, false)
					})
				}

				this.cache.$stats.removeClass(this.settings.cssClasses.hidden)

				if (this.settings.locations.length > 0) {
					this.setupMapView()
				}

				this.state.isInitialized = true
				return this
			} catch (error) {
				console.error('ArdentLocations: Initialization failed', error)
				return this
			}
		}

		/**
		 * Cache frequently accessed DOM elements
		 * @private
		 */
		cacheElements() {
			const sel = this.settings.selectors

			this.cache = {
				$element: this.$element,
				$stats: this.$element.find(sel.stats),
				$list: this.$element.find(sel.list),
				$map: this.$element.find(sel.map),
				$items: this.$element.find(sel.item),
				$noMatches: this.$element.find(sel.noMatches),
				$count: this.$element.find(sel.count),
				$filters: $(sel.filters),
				$listings: this.$element.find(sel.listings),
			}
		}

		/**
		 * Initialize Google Maps objects
		 * @private
		 */
		initializeGoogleMaps() {
			this.bounds = new window.google.maps.LatLngBounds()
			this.infowindow = new window.google.maps.InfoWindow()
		}

		/**
		 * Bind event handlers
		 * @private
		 */
		bindEvents() {
			// Browser back/forward navigation
			this.eventHandlers.popstate = () => this.syncWithURL(true)
			window.addEventListener('popstate', this.eventHandlers.popstate)
		}

		/**
		 * Create filter UI elements
		 * @private
		 */
		createFilters() {
			Object.values(this.settings.filters).forEach(filter => {
				if (filter.enabled) {
					this.createFilterList(filter)
				}
			})
		}

		/**
		 * Create individual filter list
		 * @private
		 */
		createFilterList(filter) {
			// Setup clear button
			const clearSelector = `.filter__clear[data-filter="${filter.dataFilter}"]`
			const $clearBtn = $(clearSelector)

			if ($clearBtn.length) {
				$clearBtn.on('click', () => this.clearFilter(filter.dataFilter))
			}

			// Setup filter radio buttons
			filter.data.forEach(item => {
				const radioSelector = `input[type=radio][name="${filter.dataFilter}"][value="${item.key}"]`
				const $radio = $(radioSelector)

				if ($radio.length) {
					$radio.on('change', e => {
						this.setFilter(e.target.name, e.target.value)
					})
				}
			})

			// Setup "Show More" toggle if needed
			if (filter.data.length > this.settings.maxVisibleFilters) {
				const $showMoreLink = $(`.show-more-${filter.dataFilter} a`)

				if ($showMoreLink.length) {
					$showMoreLink.on('click', e => {
						this.toggleFilterVisibility(e, e.currentTarget, filter.dataFilter)
					})
				}
			}
		}

		/**
		 * Toggle filter visibility (Show More/Less)
		 * @private
		 */
		toggleFilterVisibility(event, element, target) {
			event.preventDefault()

			const $el = $(element)
			const toggle = $el.data('toggle')
			const { fadeIn, fadeOut, hidden } = this.settings.cssClasses

			if (toggle === 'more') {
				$(`label[for^="${target}"][data-show="more"]`)
					.removeClass(`${hidden} ${fadeOut}`)
					.addClass(fadeIn)
					.attr('data-show', 'less')

				$el.data('toggle', 'less').text('Show Less')
			} else {
				$(`label[for^="${target}"][data-show="less"]`)
					.addClass(hidden)
					.removeClass(fadeIn)
					.addClass(fadeOut)
					.attr('data-show', 'more')

				$el.data('toggle', 'more').text('Show More')

				// Scroll to filter heading
				const $heading = $(`#${target}-heading`)
				if ($heading.length) {
					$heading[0].scrollIntoView({ behavior: 'smooth' })
				}
			}
		}

		/**
		 * Sync filters with URL parameters
		 * @private
		 */
		syncWithURL(shouldScroll = false) {
			const urlParams = new URLSearchParams(window.location.search)

			this.clearAllFilters()

			// Apply filters from URL and update state
			Object.values(this.settings.filters).forEach(filter => {
				if (filter.enabled) {
					const paramName = this.toKebabCase(filter.dataFilter)
					const paramValue = urlParams.get(paramName)

					if (paramValue) {
						this.setFilter(filter.dataFilter, paramValue, false)
					}
				}
			})

			// Handle pagination
			if (this.settings.pagination.enabled) {
				const pageParam = urlParams.get('pg')
				if (pageParam) {
					this.settings.pagination.paged = true
					this.state.currentPage = parseInt(pageParam, 10)
				} else {
					this.settings.pagination.paged = false
					this.state.currentPage = 1
				}
			}

			this.applyFilters(true)

			if (shouldScroll) {
				this.scrollToStats()
			}
		}

		/**
		 * Set a filter value
		 * @public
		 */
		setFilter(filterName, value = '', shouldScroll = true, resetOthers = false) {
			if (resetOthers) {
				this.clearAllFilters()
			}

			const { hidden, disabled } = this.settings.cssClasses

			// Update state
			this.state.activeFilters[filterName] = value

			// Show clear button
			$(`.location-filters__${filterName} .filter__clear`).removeClass(hidden)

			// Update radio buttons
			const $radios = $(`input[name*="${filterName}"]`)

			$radios.each((index, radio) => {
				const $radio = $(radio)
				const $label = $radio.parent()

				if (radio.value === value) {
					$radio.prop('checked', true).prop('disabled', false)
					$label.removeClass(disabled)

					// Add filter tag
					if (filterName === radio.name) {
						this.addFilterTag(filterName, $radio.next().html())
					}
				} else {
					$radio.prop('disabled', true)
					$label.addClass(disabled)
				}
			})

			this.applyFilters()

			if (shouldScroll) {
				this.scrollToStats()
			}

			// Emit custom event for external listeners (like mobile filters)
			this.$element.trigger('filterChanged', { filterName, value })
		}

		/**
		 * Clear a specific filter
		 * @public
		 */
		clearFilter(filterName, shouldScroll = true) {
			const { hidden, disabled } = this.settings.cssClasses
			let filterValue = ''

			// Reset radio buttons
			const $radios = $(`input[name*="${filterName}"]`)
			$radios.each((index, radio) => {
				const $radio = $(radio)
				if ($radio.prop('checked')) {
					filterValue = radio.value
				}
				$radio.prop('checked', false).prop('disabled', false)
				$radio.parent().removeClass(disabled)
			})

			// Hide clear button
			$(`.filter__clear[data-filter="${filterName}"]`).addClass(hidden)

			// Remove filter tag
			this.cache.$filters.find(`span[data-filter="${filterName}"]`).remove()

			// Clear from state
			delete this.state.activeFilters[filterName]

			// Clear typeahead if it matches
			if (this.state.typeahead.filterType === filterName && filterValue === this.state.typeahead.filterKey) {
				this.clearTypeahead()
			}

			this.applyFilters()

			if (shouldScroll) {
				this.scrollToStats()
			}

			// Emit custom event for external listeners (like mobile filters)
			this.$element.trigger('filterCleared', { filterName })
		}

		/**
		 * Clear all filters
		 * @public
		 */
		clearAllFilters() {
			const { hidden, disabled } = this.settings.cssClasses

			Object.values(this.settings.filters).forEach(filter => {
				if (filter.enabled) {
					const $radios = $(`input[name*="${filter.dataFilter}"]`)
					$radios.prop('checked', false).prop('disabled', false)
					$radios.parent().removeClass(disabled)

					$(`.filter__clear[data-filter="${filter.dataFilter}"]`).addClass(hidden)
				}
			})

			this.cache.$filters.find('.location__filters__filter').remove()

			// Clear state
			this.state.activeFilters = {}

			// Emit custom event for external listeners (like mobile filters)
			this.$element.trigger('filtersCleared')
		}

		/**
		 * Apply current filters to locations
		 * @public
		 */
		applyFilters(bypassHistory = false) {
			const filteredLocations = this.getFilteredLocations()
			this.updateFilterUI(filteredLocations)
			this.updateMapMarkers(filteredLocations)
			this.updateLocationList(filteredLocations)
			this.updateStats(filteredLocations.length)
			this.paginate()

			if (!bypassHistory) {
				this.updateURL()
			}
		}

		/**
		 * Get filtered locations based on active filters
		 * @private
		 */
		getFilteredLocations() {
			let locations = [...this.settings.locations]

			// Use state.activeFilters instead of DOM queries
			Object.entries(this.state.activeFilters).forEach(([filterName, filterValue]) => {
				locations = locations.filter(location => {
					const locationValue = location[filterName]
					return Array.isArray(locationValue)
						? locationValue.includes(filterValue)
						: this.toKebabCase(locationValue) === filterValue
				})
			})

			return locations
		}

		/**
		 * Update filter UI based on available locations
		 * @private
		 */
		updateFilterUI(filteredLocations) {
			Object.values(this.settings.filters).forEach(filter => {
				if (!filter.enabled) return

				const $selectedFilter = $(`[type=radio][name^="${filter.dataFilter}"]:checked`)
				const filterGroups = this.categorizeFilters(filter, filteredLocations, $selectedFilter.length > 0)

				this.renderFilterGroups(filter, filterGroups)
			})
		}

		/**
		 * Categorize filters into available and unavailable
		 * @private
		 */
		categorizeFilters(filter, filteredLocations, hasSelection) {
			const available = []
			const unavailable = []

			if (hasSelection) {
				const $selectedFilter = $(`[type=radio][name^="${filter.dataFilter}"]:checked`)
				available.push($selectedFilter[0])

				$(`[type=radio][name^="${filter.dataFilter}"]:not(:checked)`).each((index, radio) => {
					unavailable.push(radio)
				})
			} else {
				filter.data.forEach(item => {
					const hasLocations = filteredLocations.some(location => {
						const filterValue = location[filter.dataFilter]
						return Array.isArray(filterValue) ? filterValue.includes(item.key) : filterValue === item.value
					})

					const radios = $(`[type=radio][name^="${filter.dataFilter}"][value="${item.key}"]`).toArray()

					if (hasLocations) {
						available.push(...radios)
					} else {
						unavailable.push(...radios)
					}
				})
			}

			return { available, unavailable }
		}

		/**
		 * Render filter groups in the UI
		 * IMPORTANT: The counter is shared across BOTH available AND unavailable filters
		 * @private
		 */
		renderFilterGroups(filter, groups) {
			const { disabled, hidden, fadeIn, fadeOut } = this.settings.cssClasses
			const $showMoreLink = $(`.show-more-${filter.dataFilter} a`)
			const showMore = $showMoreLink.length ? $showMoreLink.data('toggle') : ''

			// SINGLE shared counter for ALL filters (available + unavailable combined)
			let filterLoop = 0

			// Process available filters first
			groups.available.forEach(radio => {
				const $radio = $(radio)
				const $label = $radio.parent()

				$radio.prop('disabled', false)
				$label.removeClass(disabled).removeClass(`${fadeIn} ${fadeOut}`)

				// Hide if counter exceeds maxVisibleFilters (shared across all filter types)
				if (filterLoop >= this.settings.maxVisibleFilters) {
					$label.attr('data-show', showMore)
					$label.toggleClass(hidden, showMore === 'more')
				} else {
					$label.attr('data-show', '').removeClass(hidden)
				}

				// Append to correct container
				const isMobile = $label.attr('for')?.startsWith('mobile-')
				const container = isMobile
					? `.location__mobile-filters .location-filters__${filter.dataFilter} .location-filters__filters`
					: `.location__filters .location-filters__${filter.dataFilter} .location-filters__filters`

				$(container).append($label)
				filterLoop++ // Increment shared counter
			})

			// Continue with unavailable filters using THE SAME counter
			groups.unavailable.forEach(radio => {
				const $radio = $(radio)
				const $label = $radio.parent()

				$radio.prop('disabled', true)
				$label.addClass(disabled).removeClass(`${fadeIn} ${fadeOut}`)

				// Hide if counter exceeds maxVisibleFilters (shared counter continues from above)
				if (filterLoop >= this.settings.maxVisibleFilters) {
					$label.attr('data-show', showMore)
					$label.toggleClass(hidden, showMore === 'more')
				} else {
					$label.attr('data-show', '').removeClass(hidden)
				}

				// Append to correct container
				const isMobile = $label.attr('for')?.startsWith('mobile-')
				const container = isMobile
					? `.location__mobile-filters .location-filters__${filter.dataFilter} .location-filters__filters`
					: `.location__filters .location-filters__${filter.dataFilter} .location-filters__filters`

				$(container).append($label)
				filterLoop++ // Increment shared counter (continues counting)
			})
		}

		/**
		 * Update map markers
		 * @private
		 */
		updateMapMarkers(filteredLocations) {
			// Hide all markers
			this.infowindow.close()
			this.markers.forEach(marker => {
				marker.map = null
			})

			// Show filtered markers
			const filteredMarkers = []
			filteredLocations.forEach(location => {
				const marker = this.markers.find(m => m.dataset.locationId === location.entityId)
				if (marker) {
					marker.map = this.map
					filteredMarkers.push(marker)
				}
			})

			// Adjust map bounds
			if (filteredMarkers.length > 0) {
				const bounds = new window.google.maps.LatLngBounds()
				filteredMarkers.forEach(marker => {
					bounds.extend(marker.position)
				})
				this.setMapBounds(this.map, bounds)
			}
		}

		/**
		 * Update location list visibility
		 * @private
		 */
		updateLocationList(filteredLocations) {
			const { hidden } = this.settings.cssClasses

			// Hide all items
			this.cache.$items.addClass(hidden).removeClass('pagination__hidden')

			// Show filtered items
			filteredLocations.forEach(location => {
				const $item = this.$element.find(`[data-location-id="${location.entityId}"]`)
				$item.removeClass(hidden)
			})
		}

		/**
		 * Update statistics display
		 * @private
		 */
		updateStats(count) {
			const { hidden } = this.settings.cssClasses
			const totalItems = this.cache.$items.not(this.cache.$noMatches).length
			const hiddenItems = this.cache.$items.not(this.cache.$noMatches).filter(`.${hidden}`).length

			// Toggle "no matches" message
			this.cache.$noMatches.toggleClass(hidden, totalItems !== hiddenItems)

			// Update count
			if (this.cache.$count.length) {
				this.cache.$count.text(`${count} Results Found`)
			}
		}

		/**
		 * Handle pagination
		 * @private
		 */
		paginate() {
			if (!this.settings.pagination.enabled) return

			const $pagination = this.$element.find('.pagination')
			$pagination.remove()

			const $visibleItems = this.cache.$items
				.not(`.${this.settings.cssClasses.hidden}`)
				.not(this.cache.$noMatches)

			if ($visibleItems.length <= this.settings.pagination.pageCount) return

			let page = this.state.currentPage
			let shouldScroll = false

			if (this.settings.pagination.paged) {
				shouldScroll = true
				this.settings.pagination.paged = false
			} else {
				this.state.currentPage = 1
				page = 1
			}

			const pageCount = this.settings.pagination.pageCount
			const startIndex = (page - 1) * pageCount
			const endIndex = page * pageCount
			const totalPages = Math.ceil($visibleItems.length / pageCount)

			// Hide all items for pagination
			$visibleItems.addClass('pagination__hidden')

			// Show current page items
			$visibleItems.slice(startIndex, endIndex).removeClass('pagination__hidden')

			// Create pagination controls
			this.createPaginationControls(totalPages, page)

			if (shouldScroll) {
				this.scrollToStats()
			}
		}

		/**
		 * Create pagination controls
		 * @private
		 */
		// createPaginationControls(totalPages, currentPage) {
		// 	const $pagination = $('<div class="pagination"></div>')

		// 	for (let i = 1; i <= totalPages; i++) {
		// 		const $link = $('<a class="pagination__link"></a>')
		// 			.text(i)
		// 			.data('page', i)
		// 			.toggleClass('current', i === currentPage)
		// 			.on('click', e => {
		// 				e.preventDefault()
		// 				this.handlePaginationClick($(e.currentTarget).data('page'))
		// 			})

		// 		$pagination.append($link)
		// 	}

		// 	this.cache.$list.append($pagination)
		// }

		createPaginationControls(totalPages, currentPage) {
			// Create the accessible wrapper: <nav> containing a <ul>
			const $nav = $('<nav aria-label="Pagination"></nav>')
			const $pagination = $('<ul class="pagination"></ul>')

			for (let i = 1; i <= totalPages; i++) {
				const isCurrent = i === currentPage

				// Build the link
				const $link = $('<a class="pagination__link"></a>')
					.text(i)
					.data('page', i)
					.toggleClass('current', isCurrent)
					.attr('href', '#') // For Accessibilty focusable links
					.on('click', e => {
						e.preventDefault()
						this.handlePaginationClick($(e.currentTarget).data('page'))
					})

				// Accessibility: Tell screen readers which page is active
				if (isCurrent) {
					$link.attr('aria-current', 'page')
				}

				// Wrap the link in an <li> and append it to the <ul>
				const $li = $('<li></li>').append($link)
				$pagination.append($li)
			}

			// Append the <ul> into the <nav>, then append to list
			$nav.append($pagination)
			this.cache.$list.append($nav)
		}

		/**
		 * Handle pagination link click
		 * @private
		 */
		handlePaginationClick(page) {
			this.state.currentPage = page
			this.settings.pagination.paged = true

			const urlParams = new URLSearchParams(window.location.search)

			if (page === 1) {
				urlParams.delete('pg')
			} else {
				urlParams.set('pg', page)
			}

			const newUrl = urlParams.toString() ? `?${urlParams.toString()}` : window.location.pathname
			history.pushState(null, null, newUrl)

			this.paginate()
		}

		/**
		 * Display a single location
		 * @public
		 */
		displayLocation(location, bypassHistory = false) {
			const { hidden } = this.settings.cssClasses

			// Hide all items
			this.cache.$items.addClass(hidden).removeClass('pagination__hidden')

			// Close info window and hide all markers
			this.infowindow.close()
			this.markers.forEach(marker => {
				marker.map = null
			})

			// Show selected location
			const $locationItem = this.$element.find(`[data-location-id="${location.key}"]`)
			$locationItem.removeClass(hidden)

			// Show marker
			const marker = this.markers.find(m => m.dataset.locationId === location.key)
			if (marker) {
				marker.map = this.map
				const bounds = new window.google.maps.LatLngBounds()
				bounds.extend(marker.position)
				this.setMapBounds(this.map, bounds)
			}

			// Update stats
			const visibleCount = this.$element.find(`[data-location-id="${location.key}"]`).length
			this.updateStats(visibleCount)

			this.paginate()

			if (!bypassHistory) {
				this.updateURL()
			}
		}

		/**
		 * Setup typeahead search
		 * @private
		 */
		setupTypeahead() {
			const $searchInput = $('#searchInput')
			const $searchForm = $('#ardent-location-search')
			const $resetButton = $('.typeahead_button')

			if (!$searchInput.length) return

			const typeaheadData = this.buildTypeaheadData()

			if (typeaheadData.length === 0) return

			typeahead({
				input: $searchInput[0],
				source: {
					local: typeaheadData,
					keys: ['value'],
					identity: item => `${item.key}`,
					groupKey: 'sortType',
				},
				limit: 100,
				highlight: true,
				preventSubmit: true,
				onSubmit: (e, selectedSuggestion) => {
					e.preventDefault()
					if (selectedSuggestion) return
				},
				display: (item, event) => {
					if (event) {
						this.handleTypeaheadSelection(item)
						$resetButton.removeClass(this.settings.cssClasses.hidden)
						$searchInput.addClass('has-reset')
					}
					return item.value
				},
				templates: {
					group: groupName => this.getTypeaheadGroupTemplate(groupName),
					suggestion: item => this.getTypeaheadSuggestionTemplate(item),
					notFound: resultSet => {
						this.state.typeahead = { filterType: null, filterKey: null }
						return `<span>No results found for '${resultSet.query}'</span>`
					},
				},
				hooks: {
					updateHits: async resultSet => resultSet,
				},
			})

			// Handle form reset
			$searchForm.on('reset', e => {
				e.preventDefault()
				this.clearTypeahead()
				this.clearAllFilters()
				this.applyFilters()
			})
		}

		/**
		 * Build typeahead data from locations and filters
		 * @private
		 */
		buildTypeaheadData() {
			const data = []

			// Add locations
			this.settings.locations.forEach(location => {
				data.push({
					key: location.entityId,
					value: location.locationName,
					filterType: 'location',
					filterTitle: 'Locations',
					sortType: 'a',
					...location,
				})
			})

			// Add filters
			Object.values(this.settings.filters).forEach(filter => {
				if (filter.enabled && filter.dataFilter !== 'locationName') {
					const sortType = this.getFilterSortType(filter.dataFilter)

					filter.data.forEach(item => {
						data.push({
							...item,
							filterType: filter.dataFilter,
							filterTitle: filter.title,
							sortType,
						})
					})
				}
			})

			return data
		}

		/**
		 * Get sort type for filter (for grouping)
		 * @private
		 */
		getFilterSortType(filterType) {
			const sortMap = {
				serviceLines: 'b',
				locationTypes: 'c',
			}
			return sortMap[filterType] || 'd'
		}

		/**
		 * Handle typeahead selection
		 * @private
		 */
		handleTypeaheadSelection(item) {
			this.state.typeahead = {
				filterKey: item.key,
				filterType: item.filterType,
			}

			if (item.filterType === 'location') {
				this.clearAllFilters()
				this.displayLocation(item)
			} else {
				this.setFilter(item.filterType, item.key, true, true)
			}
		}

		/**
		 * Clear typeahead search
		 * @private
		 */
		clearTypeahead() {
			const $searchInput = $('#searchInput')
			const $resetButton = $('.typeahead_button')

			this.state.typeahead = { filterType: null, filterKey: null }
			$searchInput.val('').removeClass('has-reset')
			$resetButton.addClass(this.settings.cssClasses.hidden)
		}

		/**
		 * Get typeahead group template
		 * @private
		 */
		getTypeaheadGroupTemplate(groupName) {
			const groupMap = {
				a: { label: 'Locations', attribute: 'location' },
				b: { label: 'Treatments and Services', attribute: 'serviceLines' },
				c: { label: 'Location Type', attribute: 'locationTypes' },
				d: { label: 'City Name', attribute: 'city' },
			}

			const group = groupMap[groupName] || { label: 'Other', attribute: 'other' }
			return `<div class="custom-group" data-filter="${group.attribute}">${group.label}</div>`
		}

		/**
		 * Get typeahead suggestion template
		 * @private
		 */
		getTypeaheadSuggestionTemplate(item) {
			if (item.filterType === 'location') {
				const addressParts = [item.address1, item.address2].filter(Boolean).join(' ')
				const localityParts = [item.city, item.state].filter(Boolean).join(', ')
				const address = [addressParts, localityParts, item.zip].filter(Boolean).join(' ')

				return `<div class="tt-suggestion" data-key="${item.key}">
					${item.locationName}
					<span class="tt-suggestion-alt">${address}</span>
				</div>`
			}

			return `<div class="tt-suggestion" data-key="${item.key}">${item.value}</div>`
		}

		/**
		 * Add filter tag to UI
		 * @private
		 */
		addFilterTag(filterName, label) {
			const $tag = $('<span class="location__filters__filter"></span>')
				.attr('data-filter', filterName)
				.html(label)
				.on('click', e => {
					this.clearFilter($(e.currentTarget).data('filter'))
				})

			this.cache.$filters.append($tag)
		}

		/**
		 * Update URL with current filter state
		 * @private
		 */
		updateURL() {
			const params = new URLSearchParams()

			// Use state.activeFilters instead of DOM queries
			Object.entries(this.state.activeFilters).forEach(([filterName, filterValue]) => {
				params.set(this.toKebabCase(filterName), filterValue)
			})

			// Add pagination parameter
			if (this.state.currentPage > 1) {
				params.set('pg', this.state.currentPage)
			}

			const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname
			history.pushState(null, null, newUrl)
		}

		/**
		 * Setup map view controls and initialize map
		 * @private
		 */
		setupMapView() {
			const $viewControls = $('.location__view_control span')
			$viewControls.parent().removeClass(this.settings.cssClasses.hidden)

			$viewControls.on('click', e => {
				this.toggleView($(e.currentTarget))
			})

			const $mapContainer = $('<div class="location__map hide" id="location__map"></div>')
			this.cache.$map = $mapContainer
			this.cache.$listings.append($mapContainer)

			this.initMap()
		}

		/**
		 * Toggle between map and list view
		 * @public
		 */
		toggleView($element) {
			const view = $element.data('view')
			const { hidden, selected } = this.settings.cssClasses

			$('.location__view_control span').removeClass(selected)
			$element.addClass(selected)

			this.cache.$map.addClass(hidden)
			this.cache.$list.addClass(hidden)

			if (view === 'map') {
				this.cache.$map.removeClass(hidden)
			} else {
				this.cache.$list.removeClass(hidden)
			}
		}

		/**
		 * Initialize Google Map
		 * @private
		 */
		initMap() {
			const $mapElement = $('#location__map')
			if (!$mapElement.length) return

			const gestureType = this.isTouchDevice() ? 'cooperative' : 'greedy'
			const { Map } = this.mapComponents

			this.map = new Map($mapElement[0], {
				mapTypeControl: true,
				mapTypeControlOptions: {
					style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
					position: window.google.maps.ControlPosition.TOP_CENTER,
				},
				zoomControl: true,
				zoomControlOptions: {
					position: window.google.maps.ControlPosition.RIGHT_CENTER,
				},
				scaleControl: true,
				streetViewControl: true,
				streetViewControlOptions: {
					position: window.google.maps.ControlPosition.LEFT_TOP,
				},
				fullscreenControl: true,
				gestureHandling: gestureType,
				mapId: this.targetId,
			})

			this.setupMapZoomLimits()
			this.createMarkers()
			this.setMapBounds(this.map, this.bounds)
			this.setupMapBoundsListener()
		}

		/**
		 * Setup map zoom limits
		 * @private
		 */
		setupMapZoomLimits() {
			window.google.maps.event.addListener(this.map, 'zoom_changed', () => {
				const zoomChangeBoundsListener = window.google.maps.event.addListener(
					this.map,
					'bounds_changed',
					() => {
						const zoom = this.map.getZoom()

						if (zoom > 15 && this.map.initialZoom) {
							this.map.setZoom(15)
							this.map.initialZoom = false
						}

						if (zoom < 3 && this.map.initialZoom) {
							this.map.setZoom(3)
							this.map.initialZoom = false
						}

						window.google.maps.event.removeListener(zoomChangeBoundsListener)
					}
				)
			})
		}

		/**
		 * Create map markers for all locations
		 * @private
		 */
		createMarkers() {
			const { AdvancedMarkerElement, PinElement } = this.mapComponents

			this.settings.locations.forEach(location => {
				if (!location.latitude || !location.longitude) return

				const pin = new PinElement()

				// Apply custom colors if available
				const colors = this.settings.mapSettings.colors
				if (colors && Object.keys(colors).length > 0) {
					if (colors.primary) pin.background = colors.primary
					if (colors.secondary) pin.glyphColor = colors.secondary
					if (colors.border) pin.borderColor = colors.border
				}

				const marker = new AdvancedMarkerElement({
					position: new window.google.maps.LatLng(location.latitude, location.longitude),
					map: this.map,
					content: pin.element,
				})

				marker.dataset.locationId = location.entityId
				marker.dataset.location = location

				// Add click listener for info window
				window.google.maps.event.addListener(marker, 'click', () => {
					const content = this.getInfoWindowContent(location)
					this.infowindow.setContent(content)
					this.infowindow.open(this.map, marker)
				})

				this.markers.push(marker)
				this.bounds.extend(marker.position)
			})
		}

		/**
		 * Setup map bounds change listener (with debounce)
		 * @private
		 */
		setupMapBoundsListener() {
			const debouncedFilter = this.debounce(() => {
				const filteredMarkers = this.markers.filter(marker => {
					const bounds = this.map.getBounds()
					return bounds && bounds.contains(marker.position) && marker.map !== null
				})

				this.filterLocationListByBounds(filteredMarkers)
			}, this.settings.debounceDelay)

			this.eventHandlers.boundsChanged = window.google.maps.event.addListener(
				this.map,
				'bounds_changed',
				debouncedFilter
			)
		}

		/**
		 * Filter location list based on map bounds
		 * @private
		 */
		filterLocationListByBounds(markers) {
			if (!markers.length) return

			const bounds = new window.google.maps.LatLngBounds()
			markers.forEach(marker => {
				marker.map = this.map
				bounds.extend(marker.position)
			})
		}

		/**
		 * Get info window content for a location
		 * @private
		 */
		getInfoWindowContent(location) {
			let addressHtml = ''
			let phoneHtml = ''

			if (location.address1 && location.state) {
				const streetAddress = [location.address1, location.address2].filter(Boolean).join('<br>')
				const locality = [location.city, location.state].filter(Boolean).join(', ')

				addressHtml = `
					<p>
						<a class="location__item__address"
						   itemprop="address"
						   itemscope
						   itemtype="https://schema.org/PostalAddress"
						   href="https://www.google.com/maps/dir/?api=1&destination=${location.latitude}+${location.longitude}"
						   target="_blank">
							<span itemprop="streetAddress" class="streetAddress">${streetAddress}</span>
							<span itemprop="addressLocality">${locality}</span>
							${location.zip ? `<span itemprop="postalCode">${location.zip}</span>` : ''}
						</a>
					</p>
				`
			}

			if (location.phone) {
				const phoneDigits = location.phone.replace(/\D+/g, '')
				phoneHtml = `
					<p>
						<a class="location__item__phone"
						   itemprop="telephone"
						   href="tel:${phoneDigits}">
							<svg class="location__item__phone-icon location__item__icon" viewBox="0 0 512 512">
								<use href="#location_phone"></use>
							</svg>&nbsp;${location.phone}
						</a>
					</p>
				`
			}

			return `
				<div class="info-window-container" itemscope itemtype="https://schema.org/Organization">
					<p class="info-window-container__title">
						<a class="info-window-container__title-anchor" href="${location.locationUrl}">
							<h3 itemprop="name">${location.locationName}</h3>
						</a>
					</p>
					${addressHtml}
					${phoneHtml}
					<p class="info-window-container__cta">
						<a class="button secondary expanded" href="${location.locationUrl}">
							${__('Learn More', 'ahms')}
						</a>
					</p>
				</div>
			`
		}

		/**
		 * Set map bounds with padding
		 * @private
		 */
		setMapBounds(map, bounds) {
			map.initialZoom = true
			const padding = window.innerWidth <= 1031 ? 20 : 100
			map.fitBounds(bounds, padding)
		}

		/**
		 * Scroll to stats section
		 * @private
		 */
		scrollToStats() {
			if (this.cache.$stats.length) {
				this.cache.$stats[0].scrollIntoView({ behavior: 'smooth' })
			}
		}

		/**
		 * Check if device is touch-enabled
		 * @private
		 */
		isTouchDevice() {
			return 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0
		}

		/**
		 * Convert string to kebab-case
		 * @private
		 */
		toKebabCase(str) {
			if (!str) return ''
			return (
				str
					// Handle hyphens with spaces around them (e.g., "some - text" → "some-text")
					.replace(/\s*-\s*/g, '-')
					// Replace underscores with spaces (for snake_case)
					.replace(/_/g, ' ')
					// Insert a space before uppercase letters that follow lowercase letters or numbers
					// (e.g., "camelCase" → "camel Case")
					.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
					// Insert a space before uppercase letters that are followed by lowercase letters
					// and follow other uppercase letters (e.g., "XMLParser" → "XML Parser")
					.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
					// Convert everything to lowercase
					.toLowerCase()
					// Replace spaces and any non-alphanumeric characters (except hyphens) with hyphens
					.replace(/[^a-z0-9-]+/g, '-')
					// Remove leading and trailing hyphens
					.replace(/^-+|-+$/g, '')
					// Replace multiple consecutive hyphens with a single hyphen
					.replace(/-+/g, '-')
			)
		}

		/**
		 * Debounce function
		 * @private
		 */
		debounce(func, wait) {
			let timeout
			return function executedFunction(...args) {
				const later = () => {
					clearTimeout(timeout)
					func(...args)
				}
				clearTimeout(timeout)
				timeout = setTimeout(later, wait)
			}
		}

		/**
		 * Destroy the plugin instance and clean up
		 * @public
		 */
		destroy() {
			// Remove event listeners
			if (this.eventHandlers.popstate) {
				window.removeEventListener('popstate', this.eventHandlers.popstate)
			}

			// Clear map listeners
			if (this.map) {
				window.google.maps.event.clearInstanceListeners(this.map)
			}

			// Clear marker listeners
			this.markers.forEach(marker => {
				window.google.maps.event.clearInstanceListeners(marker)
			})

			// Remove jQuery event handlers
			this.$element.find('*').off()

			// Clear cached elements
			this.cache = {}

			// Clear markers and map
			this.markers = []
			this.map = null

			// Remove plugin data
			this.$element.removeData('ardentLocations')

			this.state.isInitialized = false
		}

		/**
		 * Get current plugin state (for debugging)
		 * @public
		 */
		getState() {
			return {
				...this.state,
				markerCount: this.markers.length,
				locationCount: this.settings.locations.length,
				isInitialized: this.state.isInitialized,
			}
		}
	}

	/**
	 * jQuery plugin wrapper
	 */
	$.fn.ardentLocations = function (options, Map, AdvancedMarkerElement, PinElement) {
		return this.each(function () {
			const $this = $(this)

			if (!$this.data('ardentLocations')) {
				const instance = new ArdentLocations(this, options, Map, AdvancedMarkerElement, PinElement)
				$this.data('ardentLocations', instance.init())
				$this.trigger('locationsLoaded')
			}
		})
	}

	// Expose the class for advanced usage
	$.ardentLocations = ArdentLocations
})(jQuery)
